import { useEffect, useState } from "react";
import { CheckCircle2, Clock3, AlertTriangle, XCircle, Loader2, ShieldCheck, UserPlus, Flag, KeyRound, Mail, UserMinus, MessageCircle, Image as ImageIcon, Type } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError, STATUS_META, formatPrice, formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { PageLoading } from "@/App";
import Reveal from "@/components/Reveal";
import { toast } from "sonner";

function ModerateCard({ listing, onAction }) {
  const [feedback, setFeedback] = useState("");
  const [mode, setMode] = useState(null); // "revision" | "reject"
  const [busy, setBusy] = useState(false);

  const act = async (action, fb = null) => {
    setBusy(true);
    try {
      await onAction(listing.id, action, fb);
      setMode(null);
      setFeedback("");
    } finally {
      setBusy(false);
    }
  };

  const status = STATUS_META[listing.status];

  return (
    <div className="overflow-hidden rounded-2xl border border-black/5 bg-card shadow-sm" data-testid={`moderate-card-${listing.id}`}>
      <div className="flex flex-col sm:flex-row">
        <div className="sm:w-56 shrink-0 bg-slate-100">
          {listing.images?.[0] ? (
            <img src={listing.images[0]} alt={listing.title} className="h-44 sm:h-full w-full object-cover" />
          ) : (
            <div className="grid h-44 place-items-center font-display text-3xl font-black text-slate-300">67</div>
          )}
        </div>
        <div className="flex-1 p-5">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[10px] font-bold ${status.cls}`}>{status.label}</span>
            <span className="text-[11px] font-semibold text-slate-400">{listing.district} • {formatDate(listing.created_at)}</span>
            {listing.reports?.length > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-red-50 border border-red-200 px-2.5 py-0.5 text-[10px] font-bold text-red-600">
                <Flag size={10} /> {listing.reports.length} bildirim
              </span>
            )}
          </div>
          <h3 className="font-display font-bold leading-snug">{listing.title}</h3>
          <p className="mt-1 font-mono font-bold text-emerald-600">{formatPrice(listing.price)}</p>
          <p className="mt-2 text-xs text-slate-500 line-clamp-2 leading-relaxed">{listing.description}</p>
          {listing.feedback && (
            <p className="mt-2 text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">Önceki geri dönüş: {listing.feedback}</p>
          )}
          {listing.moderated_by && (
            <p className="mt-2 text-[11px] font-medium text-slate-500" data-testid={`moderated-by-${listing.id}`}>
              Son işlem: {listing.moderated_by} • {formatDate(listing.moderated_at)}
            </p>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            <Button data-testid={`admin-approve-button-${listing.id}`} disabled={busy} onClick={() => act("approve")} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-9 px-4 gap-1.5">
              <CheckCircle2 size={14} /> Onayla & Yayınla
            </Button>
            <Dialog open={mode === "revision"} onOpenChange={(v) => setMode(v ? "revision" : null)}>
              <DialogTrigger asChild>
                <Button data-testid={`admin-request-changes-button-${listing.id}`} disabled={busy} variant="outline" className="rounded-full text-xs h-9 px-4 gap-1.5 border-amber2/50 text-amber-700 hover:bg-amber2/10">
                  <AlertTriangle size={14} /> Güncelleme İste
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Güncelleme talebi gönder</DialogTitle></DialogHeader>
                <p className="text-sm text-slate-500">Kullanıcıya neyi düzeltmesi gerektiğini yazın. İlan revizyon durumuna alınır, kullanıcı düzenleyip tekrar gönderir.</p>
                <Textarea data-testid="revision-feedback-input" rows={3} value={feedback} onChange={(e) => setFeedback(e.target.value)} placeholder="Örn: Lütfen ürünün net bir fotoğrafını yükleyin." />
                <Button data-testid="revision-submit-button" disabled={busy || feedback.trim().length < 10} onClick={() => act("revision", feedback)} className="bg-amber-500 hover:bg-amber-600 text-white">
                  Gönder
                </Button>
              </DialogContent>
            </Dialog>
            <Dialog open={mode === "reject"} onOpenChange={(v) => setMode(v ? "reject" : null)}>
              <DialogTrigger asChild>
                <Button data-testid={`admin-reject-button-${listing.id}`} disabled={busy} variant="outline" className="rounded-full text-xs h-9 px-4 gap-1.5 border-red-200 text-red-600 hover:bg-red-50">
                  <XCircle size={14} /> Reddet
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>İlanı reddet</DialogTitle></DialogHeader>
                <p className="text-sm text-slate-500">Red gerekçesini yazın. Kullanıcı gerekçeyi hesabında görecektir.</p>
                <Textarea data-testid="reject-feedback-input" rows={3} value={feedback} onChange={(e) => setFeedback(e.target.value)} placeholder="Örn: İlan kurallara aykırı: yasaklı ürün kategorisi." />
                <Button data-testid="reject-submit-button" disabled={busy || feedback.trim().length < 10} onClick={() => act("reject", feedback)} className="bg-red-600 hover:bg-red-700 text-white">
                  Reddet
                </Button>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>
    </div>
  );
}

function AccountSettings({ user, setUser }) {
  const [emailForm, setEmailForm] = useState({ email: user.email, password: "" });
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  const saveEmail = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-email", emailForm);
      toast.success(data.detail);
      setUser({ ...user, email: data.email });
      setEmailForm((f) => ({ ...f, password: "" }));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm) {
      toast.error("Yeni şifreler birbiriyle uyuşmuyor.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      });
      toast.success(data.detail);
      setPwForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-7 grid gap-5 lg:grid-cols-2" data-testid="account-settings">
      <form onSubmit={saveEmail} className="h-fit space-y-4 rounded-2xl border border-black/5 bg-card p-6">
        <div className="flex items-center gap-2">
          <Mail size={18} className="text-coral" />
          <h3 className="font-display font-bold">E-posta Değiştir</h3>
        </div>
        <p className="text-xs text-slate-500">Giriş yaptığınız e-postayı güvenliğiniz için mevcut şifrenizle değiştirebilirsiniz.</p>
        <div className="space-y-1.5">
          <Label htmlFor="acc-email">Yeni E-posta *</Label>
          <Input id="acc-email" data-testid="account-email-input" required type="email" value={emailForm.email} onChange={(e) => setEmailForm((f) => ({ ...f, email: e.target.value }))} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="acc-email-pw">Mevcut Şifreniz *</Label>
          <Input id="acc-email-pw" data-testid="account-email-password-input" required type="password" value={emailForm.password} onChange={(e) => setEmailForm((f) => ({ ...f, password: e.target.value }))} />
        </div>
        <Button data-testid="account-email-submit-button" type="submit" disabled={busy} className="w-full rounded-full bg-coal py-3 text-sm font-bold text-bone hover:bg-coal/85 transition">
          E-postayı Güncelle
        </Button>
      </form>

      <form onSubmit={savePassword} className="h-fit space-y-4 rounded-2xl border border-black/5 bg-card p-6">
        <div className="flex items-center gap-2">
          <KeyRound size={18} className="text-coral" />
          <h3 className="font-display font-bold">Şifre Değiştir</h3>
        </div>
        <p className="text-xs text-slate-500">Yeni şifre en az 8 karakter olmalı ve harf + rakam içermelidir.</p>
        {[["current_password", "Mevcut Şifreniz *", "account-current-password-input"], ["new_password", "Yeni Şifre *", "account-new-password-input"], ["confirm", "Yeni Şifre (Tekrar) *", "account-confirm-password-input"]].map(([k, label, tid]) => (
          <div key={k} className="space-y-1.5">
            <Label htmlFor={`acc-${k}`}>{label}</Label>
            <Input id={`acc-${k}`} data-testid={tid} required type="password" minLength={k === "current_password" ? undefined : 8} value={pwForm[k]} onChange={(e) => setPwForm((f) => ({ ...f, [k]: e.target.value }))} />
          </div>
        ))}
        <Button data-testid="account-password-submit-button" type="submit" disabled={busy} className="w-full rounded-full bg-coral py-3 text-sm font-bold text-white hover:bg-coraldark transition">
          Şifreyi Güncelle
        </Button>
      </form>
    </div>
  );
}

function SiteSettingsForm({ settings, onSaved }) {
  const [form, setForm] = useState({
    whatsapp_number: settings.whatsapp_number || "",
    hero_line1: settings.hero_line1 || "",
    hero_line2: settings.hero_line2 || "",
    hero_line3: settings.hero_line3 || "",
    hero_subtitle: settings.hero_subtitle || "",
    hero_image_url: settings.hero_image_url || "",
    marquee_items: settings.marquee_items || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/admin/settings", form);
      toast.success("Site ayarları kaydedildi ve anında yayına alındı.");
      onSaved(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="mt-7 max-w-2xl space-y-6 rounded-2xl border border-black/5 bg-card p-6" data-testid="site-settings-form">
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <MessageCircle size={18} className="text-wa" />
          <h3 className="font-display font-bold">Genel WhatsApp Numarası (İletişim)</h3>
        </div>
        <p className="text-xs text-slate-500">Footer ve ana sayfadaki "WhatsApp Destek" butonları bu numaraya gider. İlan sayfalarındaki iletişim, satıcının kendi numarasıdır.</p>
        <div className="space-y-1.5">
          <Label htmlFor="wa-number">WhatsApp Numarası *</Label>
          <Input id="wa-number" data-testid="settings-whatsapp-input" required type="tel" value={form.whatsapp_number} onChange={set("whatsapp_number")} placeholder="0532 123 45 67" />
        </div>
      </div>

      <div className="space-y-3 border-t border-black/5 pt-5">
        <div className="flex items-center gap-2">
          <Type size={18} className="text-coral" />
          <h3 className="font-display font-bold">Ana Sayfa Yazıları (Hero)</h3>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          {[["hero_line1", "Başlık Satır 1", "settings-hero-line1"], ["hero_line2", "Başlık Satır 2", "settings-hero-line2"], ["hero_line3", "Başlık Satır 3 (renkli)", "settings-hero-line3"]].map(([k, label, tid]) => (
            <div key={k} className="space-y-1.5">
              <Label htmlFor={tid}>{label}</Label>
              <Input id={tid} data-testid={tid} value={form[k]} onChange={set(k)} maxLength={40} />
            </div>
          ))}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="settings-hero-subtitle">Açıklama Paragrafı</Label>
          <Textarea id="settings-hero-subtitle" data-testid="settings-hero-subtitle" rows={3} value={form.hero_subtitle} onChange={set("hero_subtitle")} />
        </div>
      </div>

      <div className="space-y-3 border-t border-black/5 pt-5">
        <div className="flex items-center gap-2">
          <ImageIcon size={18} className="text-coral" />
          <h3 className="font-display font-bold">Ana Görsel</h3>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="settings-hero-image">Görsel Bağlantısı (https:// ile başlamalı)</Label>
          <Input id="settings-hero-image" data-testid="settings-hero-image-input" value={form.hero_image_url} onChange={set("hero_image_url")} placeholder="https://images.unsplash.com/..." />
          {form.hero_image_url && (
            <img src={form.hero_image_url} alt="Önizleme" className="mt-2 h-28 w-44 rounded-xl object-cover border border-black/10" onError={(e) => { e.target.style.display = "none"; }} />
          )}
        </div>
      </div>

      <div className="space-y-3 border-t border-black/5 pt-5">
        <h3 className="font-display font-bold">Kayan Şerit (Marquee) Yazıları</h3>
        <p className="text-xs text-slate-500">Her satır ayrı bir ifade olur.</p>
        <Textarea id="settings-marquee" data-testid="settings-marquee-input" rows={4} value={form.marquee_items} onChange={set("marquee_items")} />
      </div>

      <Button data-testid="site-settings-submit-button" type="submit" disabled={busy} className="w-full rounded-full bg-coral py-3.5 text-sm font-bold text-white hover:bg-coraldark transition shadow-lg shadow-coral/25">
        {busy ? <Loader2 size={17} className="animate-spin" /> : "Kaydet & Yayına Al"}
      </Button>
    </form>
  );
}

export default function AdminPanel() {
  const { user, setUser } = useAuth();
  const { settings, update: updateSettings } = useSettings();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("queue");
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState(null);
  const [allListings, setAllListings] = useState(null);
  const [users, setUsers] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [empForm, setEmpForm] = useState({ name: "", email: "", phone: "", password: "" });

  const loadAll = async () => {
    const [s, p, a] = await Promise.all([
      api.get("/staff/stats"),
      api.get("/staff/pending"),
      api.get("/staff/listings"),
    ]);
    setStats(s.data);
    setPending(p.data);
    setAllListings(a.data);
    if (isAdmin) {
      try {
        const u = await api.get("/staff/users");
        setUsers(u.data);
      } catch (e) { setUsers([]); }
    }
  };

  useEffect(() => {
    loadAll().catch(() => toast.error("Panel verileri yüklenemedi."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const moderate = async (id, action, feedback) => {
    try {
      await api.post(`/staff/listings/${id}/moderate`, { action, feedback });
      toast.success(action === "approve" ? "İlan onaylandı ve yayınlandı." : action === "revision" ? "Güncelleme talebi gönderildi." : "İlan reddedildi.");
      await loadAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleUser = async (u) => {
    const next = u.status === "active" ? "suspended" : "active";
    if (!window.confirm(`${u.name} kullanıcısı ${next === "suspended" ? "askıya alınacak" : "tekrar aktifleştirilecek"}. Emin misiniz?`)) return;
    try {
      await api.patch(`/staff/users/${u.id}/status`, { status: next });
      toast.success("Kullanıcı durumu güncellendi.");
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const demoteUser = async (u) => {
    if (!window.confirm(`${u.name} kullanıcısının ÇALIŞAN yetkisi alınacak, normal kullanıcı olacak. Emin misiniz?`)) return;
    try {
      await api.patch(`/staff/users/${u.id}/role`, { role: "user" });
      toast.success("Yetki alındı: kullanıcı artık panele giremez.");
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const addEmployee = async (e) => {
    e.preventDefault();
    try {
      await api.post("/staff/employees", empForm);
      toast.success("Çalışan hesabı oluşturuldu.");
      setEmpForm({ name: "", email: "", phone: "", password: "" });
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (stats === null) return <PageLoading />;

  const statCards = [
    { label: "Onay Bekleyen", v: stats.pending, icon: Clock3, cls: "text-amber-500" },
    { label: "Yayında", v: stats.approved, icon: CheckCircle2, cls: "text-emerald-600" },
    { label: "Revizyon", v: stats.revision, icon: AlertTriangle, cls: "text-orange-500" },
    { label: "Reddedilen", v: stats.rejected, icon: XCircle, cls: "text-red-500" },
    { label: "Kayıtlı Kullanıcı", v: stats.users, icon: ShieldCheck, cls: "text-coral" },
  ];

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 pt-24 pb-20">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono text-xs font-bold tracking-widest text-coral">MODERASYON</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-1">Alsat67 Çalışan & Onay Paneli</h1>
          <p className="mt-2 text-sm text-slate-500">Hoş geldin {user.name} — bekleyen ilanları incele, onay ver veya güncelleme iste.</p>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald2 px-4 py-2 text-xs font-bold text-bone">
          <ShieldCheck size={14} className="text-wa" /> {isAdmin ? "Yönetici" : "Yetkili Çalışan"}
        </span>
      </div>

      <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="panel-stats-row">
        {statCards.map((s, i) => (
          <Reveal key={s.label} delay={i * 0.05}>
            <div className="rounded-2xl border border-black/5 bg-card p-4">
              <s.icon size={18} className={s.cls} />
              <p data-testid={`stat-value-${s.label.replace(/\s+/g, "-").toLowerCase()}`} className="mt-2 font-display text-2xl font-black">{s.v}</p>
              <p className="text-[11px] font-semibold text-slate-400">{s.label}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <div className="mt-8 flex gap-2 overflow-x-auto no-scrollbar" data-testid="panel-tabs">
        {[["queue", `Onay Kuyruğu (${pending?.length || 0})`], ["all", "Tüm İlanlar"], ...(isAdmin ? [["users", "Kullanıcılar"], ["settings", "Site Ayarları"]] : []), ["account", "Hesap Ayarları"]].map(([k, label]) => (
          <button
            key={k} data-testid={`panel-tab-${k}`} onClick={() => setTab(k)}
            className={`shrink-0 rounded-full border px-5 py-2.5 text-sm font-bold transition ${
              tab === k ? "bg-coral border-coral text-white shadow-lg shadow-coral/25" : "border-coal/15 bg-card hover:border-coral hover:text-coral"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "queue" && (
        <div className="mt-7 space-y-4" data-testid="pending-queue">
          {pending?.length === 0 ? (
            <div className="rounded-3xl border-2 border-dashed border-coal/15 py-16 text-center">
              <CheckCircle2 size={36} className="mx-auto text-emerald-500" />
              <p className="mt-3 font-display text-lg font-bold">Kuyruk temiz — bekleyen ilan yok</p>
              <p className="text-sm text-slate-500">Yeni ilanlar geldiğinde burada görünecek.</p>
            </div>
          ) : (
            pending?.map((l) => <ModerateCard key={l.id} listing={l} onAction={moderate} />)
          )}
        </div>
      )}

      {tab === "all" && (
        <div className="mt-7 space-y-4" data-testid="all-listings-panel">
          <div className="flex gap-2">
            {[["", "Tümü"], ["pending", "Bekleyen"], ["approved", "Yayında"], ["revision", "Revizyon"], ["rejected", "Reddedilen"]].map(([k, label]) => (
              <button
                key={k} data-testid={`all-listings-filter-${k || "all"}`} onClick={() => setStatusFilter(k)}
                className={`rounded-full px-4 py-1.5 text-xs font-bold border transition ${statusFilter === k ? "bg-coal text-bone border-coal" : "border-coal/15 hover:border-coral"}`}
              >
                {label}
              </button>
            ))}
          </div>
          {(allListings || []).filter((l) => !statusFilter || l.status === statusFilter).map((l) => (
            <ModerateCard key={l.id} listing={l} onAction={moderate} />
          ))}
        </div>
      )}

      {tab === "account" && <AccountSettings user={user} setUser={setUser} />}

      {tab === "settings" && isAdmin && settings && (
        <SiteSettingsForm settings={settings} onSaved={(s) => updateSettings(s)} />
      )}

      {tab === "users" && isAdmin && (
        <div className="mt-7 grid gap-6 lg:grid-cols-[1.4fr_0.6fr]" data-testid="users-panel">
          <div className="overflow-hidden rounded-2xl border border-black/5 bg-card">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="users-table">
                <thead>
                  <tr className="border-b border-black/5 text-left text-xs uppercase tracking-wide text-slate-400">
                    <th className="px-4 py-3">Kullanıcı</th>
                    <th className="px-4 py-3">İletişim</th>
                    <th className="px-4 py-3">Rol</th>
                    <th className="px-4 py-3">İlan</th>
                    <th className="px-4 py-3">Durum</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {(users || []).map((u) => (
                    <tr key={u.id} className="border-b border-black/5 last:border-0">
                      <td className="px-4 py-3 font-bold" data-testid={`user-name-${u.id}`}>{u.name}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">
                        {u.email}
                        <br />
                        <span className="font-mono">+{u.phone}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold ${u.role === "admin" ? "bg-coral/10 text-coral" : u.role === "employee" ? "bg-emerald2/10 text-emerald2" : "bg-slate-100 text-slate-600"}`}>
                          {u.role === "admin" ? "Yönetici" : u.role === "employee" ? "Çalışan" : "Üye"}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">{u.listing_count}</td>
                      <td className="px-4 py-3">
                        <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold ${u.status === "active" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-600 border border-red-200"}`}>
                          {u.status === "active" ? "Aktif" : "Askıda"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {u.role !== "admin" && (
                          <div className="flex flex-wrap gap-1.5">
                            <button
                              data-testid={`toggle-user-${u.id}`}
                              onClick={() => toggleUser(u)}
                              className="rounded-full border border-coal/15 px-3 py-1.5 text-[11px] font-bold hover:border-coral hover:text-coral transition"
                            >
                              {u.status === "active" ? "Askıya Al" : "Aktifleştir"}
                            </button>
                            {u.role === "employee" && (
                              <button
                                data-testid={`demote-user-${u.id}`}
                                onClick={() => demoteUser(u)}
                                className="inline-flex items-center gap-1 rounded-full border border-amber2/50 px-3 py-1.5 text-[11px] font-bold text-amber-700 hover:bg-amber2/10 transition"
                              >
                                <UserMinus size={12} /> Yetkiyi Al
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <form onSubmit={addEmployee} className="h-fit space-y-4 rounded-2xl border border-black/5 bg-card p-6" data-testid="create-employee-form">
            <div className="flex items-center gap-2">
              <UserPlus size={18} className="text-coral" />
              <h3 className="font-display font-bold">Yeni Çalışan Hesabı</h3>
            </div>
            <p className="text-xs text-slate-500 leading-relaxed">Çalışanlar onay panelinden ilanları onaylayabilir veya geri dönüş verebilir.</p>
            {[["name", "Ad Soyad", "text"], ["email", "E-posta", "email"], ["phone", "Telefon", "tel"], ["password", "Şifre", "password"]].map(([k, label, type]) => (
              <div key={k} className="space-y-1.5">
                <Label htmlFor={`emp-${k}`}>{label} *</Label>
                <Input
                  id={`emp-${k}`} data-testid={`employee-${k}-input`} required type={type} minLength={k === "password" ? 8 : 3}
                  value={empForm[k]} onChange={(e) => setEmpForm((f) => ({ ...f, [k]: e.target.value }))}
                  placeholder={k === "phone" ? "0532 123 45 67" : k === "password" ? "En az 8 karakter, harf + rakam" : ""}
                />
              </div>
            ))}
            <Button data-testid="create-employee-submit-button" type="submit" className="w-full rounded-full bg-coral py-3 text-sm font-bold text-white hover:bg-coraldark transition">
              Çalışan Oluştur
            </Button>
          </form>
        </div>
      )}
    </div>
  );
}
